/**
 * Semantic cross-checks — the airlock's layer 2 (architecture.md §6 step 3,
 * §14; plan §6 R3 scope: dangling references, duplicate ids, entry-point
 * existence).
 *
 * @remarks
 * Schemas validate SHAPE; this module validates that every id named anywhere
 * in a pack refers to something the pack declares — and that no id is
 * declared twice. Every miss is one `reference_broken` with a precise
 * `referencePath`, and ALL misses are collected (never first-failure): the
 * creator studio shows authors the whole list at once.
 *
 * Duplicate declarations are reported through the same variant, path pointed
 * at the SECOND occurrence: duplicated ids are reference integrity — they
 * make every reference to that id ambiguous.
 *
 * Deliberately NOT here: `movementIntentToken` referencing a declared intent
 * (compileLexicon owns vocabulary semantics — D14), and the deeper §14
 * checks (reachability, gate satisfiability, secrets-remain-optional), which
 * arrive with the phases that give them meaning.
 *
 * @packageDocumentation
 */

import { type ContentPackDefinition, verbInteractionArmsOf } from '@orpheus/content-schema';

import { brokenReference, collectDuplicates } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';
import {
	checkCondition,
	checkConditionalNarratives,
	checkDescriptionFragments,
	checkEveryEffect,
	checkNarrativeLines,
	type DeclaredIdSets,
	declaredIdSetsOf,
} from './reference-walkers.js';

// The failure builders moved to their own leaf (D226, the 500-line cap); every validator still reads them from here.
export { brokenReference, collectDuplicates } from './broken-reference.js';

/**
 * Run every v0 semantic cross-check against a schema-valid pack, collecting
 * ALL failures (see the module remarks for scope and philosophy).
 *
 * @param contentPack - The parsed, schema-valid pack
 * @returns Every reference/uniqueness failure found (empty = semantically clean)
 */
export const validateContentPackSemantics = (contentPack: ContentPackDefinition): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];

	// The families this validator only READS come from the shared builder;
	// the ones it also reports duplicates in are collected here.
	const declared: DeclaredIdSets = {
		...declaredIdSetsOf(contentPack),
		endingIds: collectDuplicates(
			contentPack.endings.map((ending, endingIndex) => ({
				declarationPath: `endings[${String(endingIndex)}].endingId`,
				declaredId: ending.endingId,
			})),
			'ending',
			failures,
		),
		eventIds: collectDuplicates(
			contentPack.events.map((event, eventIndex) => ({
				declarationPath: `events[${String(eventIndex)}].eventId`,
				declaredId: event.eventId,
			})),
			'event',
			failures,
		),
		exitIds: collectDuplicates(
			contentPack.rooms.flatMap((room, roomIndex) =>
				room.exits.map((exit, exitIndex) => ({
					declarationPath: `rooms[${String(roomIndex)}].exits[${String(exitIndex)}].exitId`,
					declaredId: exit.exitId,
				})),
			),
			'exit',
			failures,
		),
		flagIds: collectDuplicates(
			contentPack.flags.map((flag, flagIndex) => ({
				declarationPath: `flags[${String(flagIndex)}].flagId`,
				declaredId: flag.flagId,
			})),
			'flag',
			failures,
		),
		itemIds: collectDuplicates(
			contentPack.items.map((item, itemIndex) => ({
				declarationPath: `items[${String(itemIndex)}].itemId`,
				declaredId: item.itemId,
			})),
			'item',
			failures,
		),
		// R12: logs are declared ON their carriers — one field per item makes
		// "a log is somewhere" true by construction; only duplication is checkable.
		logIds: collectDuplicates(
			contentPack.items.flatMap((item, itemIndex) =>
				item.log === undefined
					? []
					: [{ declarationPath: `items[${String(itemIndex)}].log.logId`, declaredId: item.log.logId }],
			),
			'log',
			failures,
		),
		roomIds: collectDuplicates(
			contentPack.rooms.map((room, roomIndex) => ({
				declarationPath: `rooms[${String(roomIndex)}].roomId`,
				declaredId: room.roomId,
			})),
			'room',
			failures,
		),
		voiceLineIds: collectDuplicates(
			contentPack.voiceLines.map((voiceLine, voiceLineIndex) => ({
				declarationPath: `voiceLines[${String(voiceLineIndex)}].voiceLineId`,
				declaredId: voiceLine.voiceLineId,
			})),
			'voice line',
			failures,
		),
	};

	// Ambient items (D19): each must be declared, appear at most once in the
	// ambient list, and never ALSO be placed in a room — ambient XOR placed,
	// because placement means LOCATION and ambient means everywhere.
	const seenAmbientIds = new Set<string>();
	const placementsByItemId = new Map<string, string>();

	// R9: events are condition-gated — their conditions walk like an interaction's (their effects: `checkEveryEffect`).
	contentPack.events.forEach((event, eventIndex) => {
		event.conditions.forEach((condition, conditionIndex) => {
			checkCondition(condition, `events[${String(eventIndex)}].conditions[${String(conditionIndex)}]`, declared, failures);
		});
	});

	// R12: voice lines carry gate conditions AND conditional-text variants —
	// both walk like everything else that reads the world.
	contentPack.voiceLines.forEach((voiceLine, voiceLineIndex) => {
		voiceLine.conditions.forEach((condition, conditionIndex) => {
			checkCondition(
				condition,
				`voiceLines[${String(voiceLineIndex)}].conditions[${String(conditionIndex)}]`,
				declared,
				failures,
			);
		});
		voiceLine.pool.forEach((poolEntry, poolIndex) => {
			if (typeof poolEntry.text !== 'string') {
				poolEntry.text.variants.forEach((variant, variantIndex) => {
					checkCondition(
						variant.condition,
						`voiceLines[${String(voiceLineIndex)}].pool[${String(poolIndex)}].text.variants[${String(variantIndex)}].condition`,
						declared,
						failures,
					);
				});
			}
		});
	});

	// R11: triggers are condition-gated effect bundles too — same walk, plus
	// their own duplicate-id check (they are not in DeclaredIdSets because no
	// condition or effect references a trigger BY id — nothing arms a watcher).
	collectDuplicates(
		contentPack.triggers.map((trigger, triggerIndex) => ({
			declarationPath: `triggers[${String(triggerIndex)}].triggerId`,
			declaredId: trigger.triggerId,
		})),
		'trigger',
		failures,
	);
	contentPack.triggers.forEach((trigger, triggerIndex) => {
		trigger.conditions.forEach((condition, conditionIndex) => {
			checkCondition(
				condition,
				`triggers[${String(triggerIndex)}].conditions[${String(conditionIndex)}]`,
				declared,
				failures,
			);
		});
	});

	// D74: every narratives array's texts may be conditional — walk them all.
	contentPack.items.forEach((item, itemIndex) => {
		const itemPath = `items[${String(itemIndex)}]`;

		checkNarrativeLines(item.askNarratives, `${itemPath}.askNarratives`, declared, failures);
		// R25/D98: the carry channels walk like every other narratives array —
		// their conditional variants read the world too.
		checkNarrativeLines(item.dropNarratives, `${itemPath}.dropNarratives`, declared, failures);
		checkNarrativeLines(item.foundNarratives, `${itemPath}.foundNarratives`, declared, failures);
		checkNarrativeLines(item.inspectNarratives, `${itemPath}.inspectNarratives`, declared, failures);
		checkNarrativeLines(item.log?.narratives, `${itemPath}.log.narratives`, declared, failures);
		checkNarrativeLines(item.retakeNarratives, `${itemPath}.retakeNarratives`, declared, failures);
		checkNarrativeLines(item.searchNarratives, `${itemPath}.searchNarratives`, declared, failures);
		checkNarrativeLines(item.takeNarratives, `${itemPath}.takeNarratives`, declared, failures);
		// D135/D199: a verb refused in the thing's own words — its lines walk like any other, and its `when` is a
		// condition like any other (unwalked until D199: a typo in an `openRefusal.when` spoke the refusal forever).
		for (const [field, refusal] of [['closeRefusal', item.closeRefusal], ['openRefusal', item.openRefusal], ['takeRefusal', item.takeRefusal]] as const) {
			checkNarrativeLines(refusal?.narratives, `${itemPath}.${field}.narratives`, declared, failures);

			if (refusal?.when !== undefined) {
				checkCondition(refusal.when, `${itemPath}.${field}.when`, declared, failures);
			}
		}
		// R29: the reach refusals walk like every other narratives array.
		checkNarrativeLines(item.unreachableNarratives, `${itemPath}.unreachableNarratives`, declared, failures);
		item.useInteractions.forEach((interaction, interactionIndex) => {
			checkNarrativeLines(
				interaction.narratives,
				`${itemPath}.useInteractions[${String(interactionIndex)}].narratives`,
				declared,
				failures,
			);
		});
		// D196: an authored verb's arms speak like an interaction's.
		for (const { arm, path } of verbInteractionArmsOf(item.verbInteractions)) {
			checkNarrativeLines(arm.narratives, `${itemPath}.${path}.narratives`, declared, failures);
		}
	});
	contentPack.rooms.forEach((room, roomIndex) => {
		checkNarrativeLines(room.firstVisitNarratives, `rooms[${String(roomIndex)}].firstVisitNarratives`, declared, failures);
		room.exits.forEach((exit, exitIndex) => {
			// D158: the refusal is conditional narratives — a plain run of lines,
			// or a fallback plus variants. Every arm is lines like any other.
			checkConditionalNarratives(
				exit.lock?.refusal,
				`rooms[${String(roomIndex)}].exits[${String(exitIndex)}].lock.refusal`,
				declared,
				failures,
			);
			// D138: the reveal's lines are narrative lines like any other.
			checkNarrativeLines(
				exit.reveal?.narratives,
				`rooms[${String(roomIndex)}].exits[${String(exitIndex)}].reveal.narratives`,
				declared,
				failures,
			);
		});
	});
	contentPack.endings.forEach((ending, endingIndex) => {
		checkNarrativeLines(ending.narratives, `endings[${String(endingIndex)}].narratives`, declared, failures);
		checkNarrativeLines(ending.refusalNarratives, `endings[${String(endingIndex)}].refusalNarratives`, declared, failures);
	});
	// D165 (Calder's §5 finding): the paywall's lines take conditional text
	// like every other narrative, and a room id in a variant's condition is a
	// reference like every other — walked here, or a typo speaks the fallback
	// forever at the one moment money is asked for, with every gate saying clean.
	checkNarrativeLines(contentPack.paywall?.narratives, 'paywall.narratives', declared, failures);

	// R11: an open ending never refuses input, so authored refusal lines on one
	// could never play — the D31/D47 dead-content law, ending-side.
	contentPack.endings.forEach((ending, endingIndex) => {
		if (ending.kind === 'open' && ending.refusalNarratives !== undefined) {
			failures.push(
				brokenReference(
					`endings[${String(endingIndex)}].refusalNarratives`,
					ending.endingId,
					'an open ending never refuses input, so these authored lines could never play',
				),
			);
		}
	});

	contentPack.rooms.forEach((room, roomIndex) => {
		// D65: room descriptions take conditional variants now, and their
		// conditions are walked exactly as item ones are (D55). R18-3/D82: the
		// brief re-entry form is the same primitive and gets the same walk.
		if (typeof room.description !== 'string') {
			room.description.variants.forEach((variant, variantIndex) => {
				checkCondition(
					variant.condition,
					`rooms[${String(roomIndex)}].description.variants[${String(variantIndex)}].condition`,
					declared,
					failures,
				);
			});
		}

		if (room.briefDescription !== undefined && typeof room.briefDescription !== 'string') {
			room.briefDescription.variants.forEach((variant, variantIndex) => {
				checkCondition(
					variant.condition,
					`rooms[${String(roomIndex)}].briefDescription.variants[${String(variantIndex)}].condition`,
					declared,
					failures,
				);
			});
		}

		// D140: a fragment's condition and its text's conditions, walked like
		// every other condition — dead fragments are the D55 defect class.
		checkDescriptionFragments(room.descriptionFragments, `rooms[${String(roomIndex)}].descriptionFragments`, declared, failures);

		// R23: a conditional PLATE reads the world exactly as conditional text
		// does, so its variant conditions join the same walk. The image-id
		// references themselves are image-validation.ts's business.
		if (room.image !== undefined && typeof room.image !== 'string') {
			room.image.variants.forEach((variant, variantIndex) => {
				checkCondition(
					variant.condition,
					`rooms[${String(roomIndex)}].image.variants[${String(variantIndex)}].condition`,
					declared,
					failures,
				);
			});
		}

		room.itemPlacements.forEach((placedItemId, placementIndex) => {
			if (!placementsByItemId.has(placedItemId)) {
				placementsByItemId.set(placedItemId, `rooms[${String(roomIndex)}].itemPlacements[${String(placementIndex)}]`);
			}
		});
	});
	contentPack.ambientItemIds.forEach((ambientItemId, ambientIndex) => {
		const ambientPath = `ambientItemIds[${String(ambientIndex)}]`;

		if (!declared.itemIds.has(ambientItemId)) {
			failures.push(brokenReference(ambientPath, ambientItemId, 'ambient list names an undeclared item'));
		}
		if (seenAmbientIds.has(ambientItemId)) {
			failures.push(brokenReference(ambientPath, ambientItemId, 'duplicate ambient declaration'));
		}
		seenAmbientIds.add(ambientItemId);

		const conflictingPlacementPath = placementsByItemId.get(ambientItemId);

		if (conflictingPlacementPath !== undefined) {
			failures.push(
				brokenReference(
					ambientPath,
					ambientItemId,
					`ambient XOR placed — this item is also placed at ${conflictingPlacementPath}; an ambient item is everywhere and nowhere`,
				),
			);
		}
	});

	// Manifest: entry point + starting inventory.
	if (!declared.roomIds.has(contentPack.manifest.entryRoomId)) {
		failures.push(
			brokenReference('manifest.entryRoomId', contentPack.manifest.entryRoomId, 'play cannot begin in an undeclared room'),
		);
	}
	contentPack.manifest.initialInventory.forEach((itemId, inventoryIndex) => {
		if (!declared.itemIds.has(itemId)) {
			failures.push(
				brokenReference(
					`manifest.initialInventory[${String(inventoryIndex)}]`,
					itemId,
					'the player cannot start holding an undeclared item',
				),
			);
		}
	});

	// Rooms: placements, exit targets, locks.
	contentPack.rooms.forEach((room, roomIndex) => {
		room.itemPlacements.forEach((itemId, placementIndex) => {
			if (!declared.itemIds.has(itemId)) {
				failures.push(
					brokenReference(
						`rooms[${String(roomIndex)}].itemPlacements[${String(placementIndex)}]`,
						itemId,
						'room places an undeclared item',
					),
				);
			}
		});
		room.exits.forEach((exit, exitIndex) => {
			const exitPath = `rooms[${String(roomIndex)}].exits[${String(exitIndex)}]`;

			// Outward exits (leadsOutOfPack, D19) declare no target — nothing to check.
			if (exit.targetRoomId !== undefined && !declared.roomIds.has(exit.targetRoomId)) {
				failures.push(brokenReference(`${exitPath}.targetRoomId`, exit.targetRoomId, 'exit leads to an undeclared room'));
			}
			// D158: the lock's own gate, and each refusal variant's, resolve like
			// every other condition — one walker, no lock-shaped special case.
			if (exit.lock?.lockedWhen !== undefined) {
				checkCondition(exit.lock.lockedWhen, `${exitPath}.lock.lockedWhen`, declared, failures);
			}

			// D233: the obstruction names the world like the bolt does.
			if (exit.lock?.blockedWhen !== undefined) {
				checkCondition(exit.lock.blockedWhen, `${exitPath}.lock.blockedWhen`, declared, failures);
			}
			if (exit.lock !== undefined && 'variants' in exit.lock.refusal) {
				exit.lock.refusal.variants.forEach((variant, variantIndex) => {
					checkCondition(variant.when, `${exitPath}.lock.refusal.variants[${String(variantIndex)}].when`, declared, failures);
				});
			}
			// D138: a hidden exit's condition resolves like any other condition.
			if (exit.reveal !== undefined) {
				checkCondition(exit.reveal.when, `${exitPath}.reveal.when`, declared, failures);
			}
		});
	});

	// Items: visibility conditions and use-interactions.
	contentPack.items.forEach((item, itemIndex) => {
		const itemPath = `items[${String(itemIndex)}]`;

		// D55: a variant whose condition names nothing is text that can never
		// speak — the same defect class D47's dead-beat rule refuses, reached
		// through the describer instead of the verb table.
		if (typeof item.description !== 'string') {
			item.description.variants.forEach((variant, variantIndex) => {
				checkCondition(variant.condition, `${itemPath}.description.variants[${String(variantIndex)}].condition`, declared, failures);
			});
		}

		checkDescriptionFragments(item.descriptionFragments, `${itemPath}.descriptionFragments`, declared, failures);

		if (item.roomVisibilityCondition !== undefined) {
			checkCondition(item.roomVisibilityCondition, `${itemPath}.roomVisibilityCondition`, declared, failures);
		}

		// R29: the reach gate is a condition like any other — a typo'd flag
		// here would silently make the glass permanent, or a formality.
		if (item.reachableWhen !== undefined) {
			checkCondition(item.reachableWhen, `${itemPath}.reachableWhen`, declared, failures);
		}

		// R29: reach refusals on an item with no barrier are lines for a
		// refusal that can never fire — the D31/D47 dead-content law,
		// reach-side (the open-ending precedent above, item-shaped).
		if (item.unreachableNarratives !== undefined && item.reachableWhen === undefined) {
			failures.push(
				brokenReference(
					`${itemPath}.unreachableNarratives`,
					item.itemId,
					'this item declares no reachableWhen, so it is never out of reach and these lines could never play',
				),
			);
		}

		// D239: a find's own words on a thing nothing hides — the same law,
		// find-side: only a concealed thing is ever found.
		if (item.foundNarratives !== undefined && item.containedIn?.concealed !== true) {
			failures.push(
				brokenReference(
					`${itemPath}.foundNarratives`,
					item.itemId,
					'this item is not concealed inside anything (`containedIn.concealed`), so no search ever finds it and these lines could never play',
				),
			);
		}
		if (item.inventoryVisibilityCondition !== undefined) {
			checkCondition(item.inventoryVisibilityCondition, `${itemPath}.inventoryVisibilityCondition`, declared, failures);
		}

		// D53: the hand-open gate is a condition like any other, and it is the one
		// standing between a player and a critical-path item — a typo'd flag id
		// here would silently make a door permanently hand-openable, which is the
		// bypass this field exists to close.
		if (item.openable?.handOpenCondition !== undefined) {
			checkCondition(item.openable.handOpenCondition, `${itemPath}.openable.handOpenCondition`, declared, failures);
		}

		// D65: the power gate is a condition standing between a device and its
		// answering at all — a typo'd flag here fails toward permanently powered.
		if (item.switchable?.poweredWhen !== undefined) {
			checkCondition(item.switchable.poweredWhen, `${itemPath}.switchable.poweredWhen`, declared, failures);
		}
		item.useInteractions.forEach((interaction, interactionIndex) => {
			const interactionPath = `${itemPath}.useInteractions[${String(interactionIndex)}]`;

			if (interaction.targetItemId !== undefined && !declared.itemIds.has(interaction.targetItemId)) {
				failures.push(
					brokenReference(`${interactionPath}.targetItemId`, interaction.targetItemId, 'interaction targets an undeclared item'),
				);
			}
			if (interaction.targetExitId !== undefined && !declared.exitIds.has(interaction.targetExitId)) {
				failures.push(
					brokenReference(`${interactionPath}.targetExitId`, interaction.targetExitId, 'interaction targets an undeclared exit'),
				);
			}
			interaction.conditions.forEach((condition, conditionIndex) => {
				checkCondition(condition, `${interactionPath}.conditions[${String(conditionIndex)}]`, declared, failures);
			});
		});
		// D196: an authored verb's arms read the world like an interaction's (their effects: `checkEveryEffect`).
		for (const { arm, path } of verbInteractionArmsOf(item.verbInteractions)) {
			arm.conditions.forEach((condition, conditionIndex) => {
				checkCondition(condition, `${itemPath}.${path}.conditions[${String(conditionIndex)}]`, declared, failures);
			});
		}
	});

	// D247: every effect the pack authors, wherever it lives — one walk, from the schema's roster of effect homes.
	checkEveryEffect(contentPack, declared, failures);

	return failures;
};

// D53: the two AUTHORING-REACHABILITY rules — "can this authored content ever
// be reached?" — live in their own module now that adding the hand-open gate
// pushed this file past the 500-line cap. They answer a different question
// from everything above, which only asks whether an id resolves. Re-exported
// so `load-game-bundle` and the suites are unchanged.
export { validateAuthoredVerbInteractions, validateAuthoredVerbNarratives } from './authored-verb-rules.js';
export { validateCarryDeclarations, validateContainment } from './authoring-reachability.js';
